import {
    ChangeDetectionStrategy,
    Component,
    isDevMode,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {
    AbstractControl,
    FormBuilder,
    FormGroup,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import {
    ActivatedRoute,
    Router,
} from '@angular/router';
import { AppStateFacade } from '@app-state';
import { PageMetadataRegistry } from '@app/router';
import {
    ControlConfig,
    FieldConfigFn,
    UpsertPageComponent,
    UpsertPageDefaultValues,
    UpsertPageValidators,
} from '@components';
import {
    FeatureFlag,
    IProcess,
    IQueueUpsert,
    QueueJsonSchema,
} from '@models';
import { TranslateService } from '@ngx-translate/core';
import { MAX_NUMBER_OF_RESULTS } from '@pages/shared/constants';
import {
    ODataQueryOptions,
    ProcessService,
    QueueItemService,
} from '@services';
import { UiSnackBarService } from '@uipath/angular/components/ui-snackbar';
import { ISuggestValueData } from '@uipath/angular/components/ui-suggest/models';
import UiValidators, { UI_DESCRIPTION_SHORT_MAX_LENGTH } from '@validators';

import {
    BehaviorSubject,
    of,
} from 'rxjs';
import {
    finalize,
    switchMap,
    takeUntil,
    tap,
} from 'rxjs/operators';

import { UPSERT_QUEUE_FIELDS } from './queue.upsert.page.models';
import { QueueUpsertPageService } from './queue.upsert.page.service';
import { UiTimeOffsetPickerComponent } from './ui-time-offset-picker/ui-time-offset-picker.component';

export interface ErrorRecord {
    error: string;
    errorMessage: string;
}

const RiskSlaLessThanSla: ValidatorFn = (control: AbstractControl) => {
    const sla = control.get('slaMinutes')!;
    const riskSla = control.get('riskSlaMinutes')!;

    if (sla !== null &&
        riskSla !== null &&
        !sla.disabled &&
        !riskSla.disabled &&
        riskSla.value > sla.value
    ) {
        const error = riskSla.errors || {};
        error.greaterThanSla = true;
        riskSla.setErrors(error);
        return error;
    }

    return null;
};
const DEFAULT_TIME_OFFSET_RANGE = {
    min: 0,
    max: 90,
};

@Component({
    templateUrl: './queue.upsert.page.html',
    styleUrls: ['./queue.upsert.page.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    providers: [QueueUpsertPageService, ProcessService, QueueItemService],
})
export class QueueUpsertPageComponent
    extends UpsertPageComponent<IQueueUpsert>
    implements OnInit {

    public get defaults(): UpsertPageDefaultValues<IQueueUpsert> {
        return {
            name: '',
            description: '',
            enforceUniqueReference: {
                value: false,
                disabled: this.isEditMode,
            },
            acceptAutoRetry: {
                value: true,
                disabled: this.isEditMode,
            },
            maxNoOfRetries: 1,
            specificDataJsonSchema: '',
            outputDataJsonSchema: '',
            analyticsDataJsonSchema: '',
            slaMinutes: 60,
            riskSlaMinutes: 60,
            triggerId: null,
        };
    }

    public get validators(): UpsertPageValidators<IQueueUpsert> {
        return {
            name: Validators.compose([
                Validators.required,
                UiValidators.whitespace,
                Validators.maxLength(50),
            ]),
            maxNoOfRetries: this.isEditMode ? [] : UiValidators.range(1, 50),
            description: Validators.maxLength(UI_DESCRIPTION_SHORT_MAX_LENGTH),
            releaseId: UiValidators.required(),
            releaseKey: UiValidators.required(),
            // 1 hour to 90 days, separate min/max for UiTimeOffSetPicker errors
            slaMinutes: Validators.compose([
                Validators.min(60),
                Validators.max(60 * 24 * 90),
            ]),
            riskSlaMinutes: Validators.compose([
                Validators.min(60),
                Validators.max(60 * 24 * 90),
            ]),
        };
    }

    public get slaErrorsToCheck() {
        return [
            {
                error: 'greaterThanSla',
                errorMessage: this._translate.instant('queue_risk_sla_less_than_sla_error'),
            },
            {
                error: 'min',
                errorMessage: this._translate.instant('time_offset_days_range_warning', DEFAULT_TIME_OFFSET_RANGE),
            },
            {
                error: 'max',
                errorMessage: this._translate.instant('time_offset_days_range_warning', DEFAULT_TIME_OFFSET_RANGE),
            },
        ] as ErrorRecord[];
    }

    private get _submit$() {
        if (!this.form) { throw new Error('Form is undefined'); }

        const formValues: IQueueUpsert = {
            ...this.form.getRawValue(),
            ...(this.form.value.acceptAutoRetry === false ?
                { maxNoOfRetries: 0 } :
                {}
            ),
        };

        // we should only clear releaseId if there is no trigger
        if (!this.triggerIsDefined && this.form.get('releaseId')!.disabled) {
            delete formValues['releaseId'];
        }

        if (this.form.get('slaMinutes')!.disabled) {
            delete formValues['slaMinutes'];
        }

        if (this.form.get('riskSlaMinutes')!.disabled) {
            delete formValues['riskSlaMinutes'];
        }

        return this.isEditMode ?
            this._upsertService.update(this.id, formValues) :
            this._upsertService.add(formValues);
    }

    @ViewChild('riskSlaTimePicker', { static: false })
    public riskSlaTimeOffsetComponent?: UiTimeOffsetPickerComponent;

    public QueueJsonSchema = QueueJsonSchema;

    public fields = UPSERT_QUEUE_FIELDS;

    public isDevMode = isDevMode();

    public form?: FormGroup;
    public specificForm?: FormGroup;

    public isQueueSchemaDefinitionsEnabled = this._appState.settings.isFeatureEnabled(FeatureFlag.QueueSchemaDefinitions);
    public isQueueSlaMonitoringEnabled = this._appState.settings.isFeatureEnabled(FeatureFlag.QueueSlaMonitoring);

    public reading$ = new BehaviorSubject<boolean>(false);

    public slaEnabled$ = new BehaviorSubject<boolean>(false);
    public riskSlaEnabled$ = new BehaviorSubject<boolean>(false);

    public triggerIsDefined = false;
    public hasQueueItems = false;

    public isProcessViewGranted$ = this._appState.user.permissions.isGranted('Processes.View');
    private _isTransactionsViewGranted$ = this._appState.user.permissions.isGranted('Transactions.View');

    private _dirtyStatus = true;
    private _initialSlaValue = 0;

    constructor(
        _route: ActivatedRoute,
        _router: Router,
        _appState: AppStateFacade,
        private _fb: FormBuilder,
        private _snack: UiSnackBarService,
        private _upsertService: QueueUpsertPageService,
        private _translate: TranslateService,
        private _process: ProcessService,
        private _pageRegistry: PageMetadataRegistry,
        private _queueItem: QueueItemService,
    ) {
        super(_route, _router, _appState);
    }

    ngOnInit() {
        super.ngOnInit();

        if (this.isEditMode) {
            this._appState.user.folder
                .change$.pipe(
                    tap(this._forceNavigateBaseUrl),
                    takeUntil(this._destroyed$),
                ).subscribe();
        }
    }

    public onAutoRetryOptionChange(enabled: boolean) {
        const maxNoOfRetries = this.getField('maxNoOfRetries');
        maxNoOfRetries[enabled ? 'enable' : 'disable']();
    }

    public onEnforceUniqueReferenceChange() {
        const enforceUnique = this.getField('enforceUniqueReference');
        enforceUnique.setValue(!enforceUnique.value);
    }

    public onReading(value: boolean) {
        this.reading$.next(value);
    }

    public onSlaToggle(state: MatSlideToggle) {
        this.slaEnabled$.next(state.checked);
    }

    public onRiskSlaToggle(state: MatSlideToggle) {
        this.riskSlaEnabled$.next(state.checked);
    }

    public onProcessSelected(event: ISuggestValueData<IProcess>) {
        this.getField('releaseId').setValue(
            event ? event.id as number : undefined,
        );
    }

    public submit() {
        if (this.isSubmitDisabled) { return; }

        this.loading$.next(true);

        this._submit$.pipe(
            finalize(() => {
                this._ignoreDirtyStatus = this._dirtyStatus;
                this.loading$.next(false);
            }),
        ).subscribe(
            () => {
                this._snack.success(this._translate.instant('save_successful'));
                this._forceNavigateBaseUrl();
            },
            error => {
                if (error.status === 403) {
                    this._forceNavigateBaseUrl();
                }
                this._dirtyStatus = false;
            },
        );
    }

    public processSearchFactory = (
        query: string = '',
        step: number = MAX_NUMBER_OF_RESULTS,
        skip = 0,
    ) => this._process.suggest(query, step, skip)


    public getProcess = (id: number) => this._process.getInstance(id);

    protected _getInstance = () =>
        this._upsertService.getInstance(this.id)

    protected _initForm(
        config: ControlConfig<IQueueUpsert>,
        _: FieldConfigFn<IQueueUpsert>,
        queue?: IQueueUpsert) {

        this.form = this._fb.group(config, { validator: RiskSlaLessThanSla });

        if (queue) {
            this.slaEnabled$.next(queue.slaMinutes > 0);
            this.riskSlaEnabled$.next(queue.riskSlaMinutes > 0);

            this._pageRegistry.setCustomTitle(queue.name);
            this.triggerIsDefined = !!queue.releaseId && !!queue.triggerId;
            this._queueItemsExist(queue.id!);
            if (queue.releaseId) {
                this.isProcessViewGranted$
                    .pipe(
                        switchMap(isGranted => isGranted ?
                            this.getProcess(queue.releaseId!) : of(null),
                        ),
                        takeUntil(this._destroyed$),
                    ).subscribe((release: IProcess | null) => {
                        if (release) {
                            this.getField('releaseKey').setValue([{ id: release.id, text: release.name }]);
                        }
                    });
            }
        }

        this._setupSlaSubscriptions();
        this.loading$.next(false);
    }

    private _queueItemsExist(id: number) {
        if (!id) {
            return;
        }

        const queryOptions: ODataQueryOptions = {
            top: 1,
            filter: {
                QueueDefinitionId: {
                    eq: id,
                },
            },
        };

        this._isTransactionsViewGranted$
            .pipe(
                switchMap(isGranted => isGranted ?
                    this._queueItem.get(queryOptions) : of(null),
                ),
                takeUntil(this._destroyed$),
            ).subscribe(item => {
                if (item && item.data && item.data.length > 0) {
                    this.hasQueueItems = true;
                }
            });
    }

    private _setupSlaSubscriptions() {
        this._initialSlaValue = this.getField('slaMinutes').value;

        this.slaEnabled$
            .pipe(
                takeUntil(this._destroyed$),
            ).subscribe(enabled => {
                const releaseId = this.getField('releaseId');
                enabled ? releaseId.enable() : releaseId.disable();

                const releaseKey = this.getField('releaseKey');
                enabled ? releaseKey.enable() : releaseKey.disable();

                const slaMinutes = this.getField('slaMinutes');
                enabled ? slaMinutes.enable() : slaMinutes.disable();

                if (!enabled) {
                    this.riskSlaEnabled$.next(false);
                }

                if (enabled && slaMinutes.value === 0) {
                    slaMinutes.setValue(60);
                } else if (!enabled) {
                    // clear all values on disable
                    slaMinutes.setValue(0);
                    if (!this.triggerIsDefined) {
                        releaseKey.setValue([]);
                        releaseId.setValue(undefined);
                    }
                }

                if (this._initialSlaValue !== slaMinutes.value) {
                    slaMinutes.markAsDirty();
                }
            });

        this.riskSlaEnabled$
            .pipe(
                takeUntil(this._destroyed$),
            ).subscribe(enabled => {
                const riskSlaMinutes = this.getField('riskSlaMinutes');
                enabled ? riskSlaMinutes.enable() : riskSlaMinutes.disable();

                // to handle edit scenarios where the value is already set
                if (enabled && riskSlaMinutes.value === 0) {
                    const slaMinutesValue = this.getField('slaMinutes').value;
                    // riskSlaValue should be 75% of slaValue by default
                    riskSlaMinutes.setValue(Math.ceil((slaMinutesValue * .75) / 60) * 60);
                } else if (!enabled) {
                    riskSlaMinutes.setValue(0);
                }

                this.form!.markAsDirty();
            });

        // required to update validity of risk SLA based on SLA
        this.getField('slaMinutes')
            .valueChanges.pipe(
                takeUntil(this._destroyed$),
            ).subscribe(_ => {
                this.getField('riskSlaMinutes').updateValueAndValidity();
                if (!this.riskSlaTimeOffsetComponent) { return; }

                this.riskSlaTimeOffsetComponent.form.get('days')!.updateValueAndValidity();
                this.riskSlaTimeOffsetComponent.form.get('hours')!.updateValueAndValidity();
            });
    }
}
